import { panUnselectedPlanObject } from "./C1_planObjectPan";
import { roomWallThickness } from "../model/C1_wallThickness";
import { C1_OpeningShading } from "./C1_OpeningShading";
import { itx } from "../../../src/translationItx/itx";
import c1_state from "../model/C1_State";
import { eloItemScaleTransform } from "./C1_eloItemScale";
import { usesFurnishingPlanPresentation } from "./C1_editModePresentation";
import type { C1_Opening } from "../types/C1_ModelTypes";
import { C1_SvgResizeGrab, type C1_ResizeGrabDirection } from "../components/C1_ResizeGrab";
import { useOnChangeWatcher } from "../../../src/models/useOnChangeWatcher";

type C1_OpeningViewProps = {
  opening: C1_Opening;
  selected: boolean;
  indirectlySelected?: boolean;
  onSelectOpening: (openingId: string) => void;
  onStartDrag: (event: React.PointerEvent<SVGGElement>, openingId: string) => void;
  onStartResize?: (event: React.PointerEvent<SVGGElement>, openingId: string, edge: "start" | "end") => void;
  onDoubleClick?: (openingId: string, clientPoint: { x: number; y: number }) => void;
  interactive?: boolean;
};

const HIT_PAD_NORMAL = 16;
const MIN_HIT_THICKNESS = 26;
const SELECTED_OPENING_COLOR = "#f59e0b";
const SELECTED_WINDOW_OUTER_FILL = "#ffedd5";
const SELECTED_WINDOW_INNER_FILL = "#fed7aa";
const FURNISHING_OPENING_LINE_COLOR = "#6b7280";

export function C1_WindowView({
  opening,
  selected,
  indirectlySelected = false,
  onSelectOpening,
  onStartDrag,
  onStartResize,
  onDoubleClick,
  interactive = true,
}: C1_OpeningViewProps) {
  useOnChangeWatcher(c1_state.settings);
  useOnChangeWatcher(opening.owner);
  const view = c1_state.view;
  const PX = c1_state.svgPx;
  const center = opening.absolutePosition;
  const horizontal = opening.side === "top" || opening.side === "bottom";
  const centerX = (center.x - view.x1) * PX;
  const centerY = (center.y - view.y1) * PX;
  const length = opening.width * PX;
  const room = c1_state.project
    .getFloor(c1_state.currentFloorId.value)
    ?.rooms.find((entry) => entry.id === opening.roomId);

  if (!room) return null;

  const thicknessM = opening.wall ? roomWallThickness(room, opening.wall, c1_state.project)
    : c1_state.project.settings.wallThicknessExterior;
  const thickness = thicknessM * PX;
  const hitThickness = Math.max(thickness, MIN_HIT_THICKNESS) + HIT_PAD_NORMAL * 2;
  const openingCursor =
    selected && c1_state.editMode.value === "building" ? (horizontal ? "ew-resize" : "ns-resize") : "default";
  const lineColor = selected
    ? SELECTED_OPENING_COLOR
    : indirectlySelected
      ? "#fdba74"
      : usesFurnishingPlanPresentation(c1_state.editMode.value)
        ? FURNISHING_OPENING_LINE_COLOR
        : "#000";
  const outerFill = selected ? SELECTED_WINDOW_OUTER_FILL : indirectlySelected ? "#fff7ed" : "#efefef";
  const innerFill = selected ? SELECTED_WINDOW_INNER_FILL : indirectlySelected ? "#ffedd5" : "lightblue";

  const x = horizontal ? centerX - length / 2 : centerX - thickness / 2;
  const y = horizontal ? centerY - thickness / 2 : centerY - length / 2;
  const width = horizontal ? length : thickness;
  const height = horizontal ? thickness : length;
  const hitX = horizontal ? centerX - length / 2 : centerX - hitThickness / 2;
  const hitY = horizontal ? centerY - hitThickness / 2 : centerY - length / 2;
  const hitWidth = horizontal ? length : hitThickness;
  const hitHeight = horizontal ? hitThickness : length;
  const innerX = horizontal ? x : centerX - thickness / 4;
  const innerY = horizontal ? centerY - thickness / 4 : y;
  const innerWidth = horizontal ? length : thickness / 2;
  const innerHeight = horizontal ? thickness / 2 : length;
  const label = String(opening.masterIndex ?? "");
  const labelWidth = Math.max(22, label.length * 6 + 10);
  const subtleMasterIndex = c1_state.floorImageRenderOptions.value?.subtleMasterIndices === true;
  const masterIndexBackground = subtleMasterIndex ? "#e5e7eb" : "#173f56";
  const masterIndexColor = subtleMasterIndex ? "#64748b" : "#fff";
  const grabOffset = 16;
  const resizeGrabs = horizontal
    ? [
        { edge: "start" as const, x: x - grabOffset, y: centerY },
        { edge: "end" as const, x: x + width + grabOffset, y: centerY },
      ]
    : [
        { edge: "start" as const, x: centerX, y: y - grabOffset },
        { edge: "end" as const, x: centerX, y: y + height + grabOffset },
      ];

  return (
    <>
      <g
        focusable="false"
        className={`C1_svgOpening C1_svgWindow${selected ? " C1_selected" : ""}${indirectlySelected ? " C1_indirectlySelected" : ""}`}
        data-c1-opening-id={opening.id}
        data-c1-room-id={opening.roomId}
        style={{
          cursor: interactive ? openingCursor : undefined,
          pointerEvents: interactive ? "all" : "none",
        }}
        onPointerDown={
          interactive
            ? (event) => {
                event.stopPropagation();
                if ((!selected || c1_state.editMode.value !== "building") && panUnselectedPlanObject(event, () => onSelectOpening(opening.id))) return;
                if (selected && c1_state.editMode.value === "building") onStartDrag(event, opening.id);
                else onSelectOpening(opening.id);
              }
            : undefined
        }
        onDoubleClick={
          interactive
            ? (event) => {
                event.preventDefault();
                event.stopPropagation();
                onDoubleClick?.(opening.id, { x: event.clientX, y: event.clientY });
              }
            : undefined
        }
      >
        <title>{itx("c1.ui.Fenster", "Fenster")}</title>

        <rect x={x} y={y} width={width} height={height} fill={outerFill} />
        {horizontal ? (
          <>
            <line x1={x} y1={y} x2={x} y2={y + height} stroke={lineColor} />
            <line x1={x + width} y1={y} x2={x + width} y2={y + height} stroke={lineColor} />
          </>
        ) : (
          <>
            <line x1={x} y1={y} x2={x + width} y2={y} stroke={lineColor} />
            <line x1={x} y1={y + height} x2={x + width} y2={y + height} stroke={lineColor} />
          </>
        )}

        <rect x={innerX} y={innerY} width={innerWidth} height={innerHeight} fill={innerFill} />
        {horizontal ? (
          <>
            <line x1={innerX} y1={innerY} x2={innerX + innerWidth} y2={innerY} stroke={lineColor} />
            <line
              x1={innerX}
              y1={innerY + innerHeight}
              x2={innerX + innerWidth}
              y2={innerY + innerHeight}
              stroke={lineColor}
            />
          </>
        ) : (
          <>
            <line x1={innerX} y1={innerY} x2={innerX} y2={innerY + innerHeight} stroke={lineColor} />
            <line
              x1={innerX + innerWidth}
              y1={innerY}
              x2={innerX + innerWidth}
              y2={innerY + innerHeight}
              stroke={lineColor}
            />
          </>
        )}

        <C1_OpeningShading shading={opening.windowShading} centerX={centerX} centerY={centerY}
          length={length} thickness={thickness} horizontal={horizontal} outwardSide={opening.outwardSide}
          selected={selected} indirectlySelected={indirectlySelected} />

        {c1_state.settings.showTags &&
          (c1_state.editMode.value === "EloConcept" || c1_state.editMode.value === "EloDetail") &&
          opening.masterIndex !== undefined && (
            <g className="C1_svgMasterIndex" pointerEvents="none" transform={eloItemScaleTransform(centerX, centerY, c1_state.settings.eloItemScale)}>
              <rect
                x={centerX - labelWidth / 2}
                y={centerY - 9}
                width={labelWidth}
                height={18}
                rx={9}
                fill={masterIndexBackground}
              />
              <text
                x={centerX}
                y={centerY}
                fill={masterIndexColor}
                fontFamily="Inter, Arial, sans-serif"
                fontSize={10}
                fontWeight={400}
                textAnchor="middle"
                dominantBaseline="central"
                alignmentBaseline="central"
              >
                {label}
              </text>
            </g>
          )}

        <rect
          className="C1_svgOpeningHitArea"
          x={hitX}
          y={hitY}
          width={hitWidth}
          height={hitHeight}
          fill="transparent"
          stroke="none"
        />
      </g>
      {selected && interactive && c1_state.editMode.value === "building" && (
        <g
          className="C1_svgWindowResizeGrabs"
          onDoubleClick={event => {
            event.preventDefault();
            event.stopPropagation();
            onDoubleClick?.(opening.id, { x: event.clientX, y: event.clientY });
          }}
          data-c1-svg-layer="window-resize-grabs"
          data-c1-opening-id={opening.id}
          pointerEvents="all"
        >
          {resizeGrabs.map((grab) => {
            const direction: C1_ResizeGrabDirection = horizontal
              ? grab.edge === "start"
                ? "left"
                : "right"
              : grab.edge === "start"
                ? "top"
                : "bottom";
            return (
              <C1_SvgResizeGrab
                sizeScale={1 / (c1_state.zoom?.value ?? 1)}
                key={grab.edge}
                data-c1-opening-id={opening.id}
                direction={direction}
                x={grab.x}
                y={grab.y}
                style={{ cursor: horizontal ? "ew-resize" : "ns-resize" }}
                onPointerDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onStartResize?.(event, opening.id, grab.edge);
                }}
              />
            );
          })}
        </g>
      )}
    </>
  );
}
